import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useLab } from '../LabContext';
import { LabBackground, NoIndex, NimnyArt, Card } from '../LabUI';

const LabLanding = () => {
  const { t, user, language, toggleLanguage } = useLab();

  // If already signed in, surface a quick continue link instead of a Sign-in CTA.
  const continueTarget = user
    ? user.role === 'parent'
      ? '/lab/parent'
      : `/lab/${user.studentId || 'max'}`
    : null;

  return (
    <div className="min-h-screen relative text-[#f5ebe0]">
      <LabBackground />
      <NoIndex />

      <div className="max-w-md mx-auto px-5 pt-5 pb-12">
        {/* URL chip */}
        <div className="flex items-center justify-center rounded-full border border-amber-300/15 bg-[#0e1530]/60 backdrop-blur-sm px-3 py-2 text-xs text-amber-200/70 font-mono">
          <span>readnimny.com/lab</span>
          <span className="mx-2 text-amber-200/40">—</span>
          <span className="text-amber-200/60">{t('privateBadge')}</span>
        </div>

        {/* Top row: brand + lang */}
        <div className="mt-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <NimnyArt size="sm" />
            <span
              className="text-xl text-[#f5ebe0]/90 lab-brand-mark"
            >
              {t('brand')}
            </span>
          </div>
          <button
            type="button"
            onClick={toggleLanguage}
            className="px-3 py-1.5 rounded-full border border-amber-300/20 text-amber-200/80 text-sm font-sans hover:bg-amber-300/5"
            data-testid="lab-landing-lang-toggle"
            aria-label="Toggle language"
          >
            {language === 'ru' ? 'EN' : 'RU'}
          </button>
        </div>

        {/* Hero */}
        <div className="mt-8 text-center">
          <h1
            className="text-4xl sm:text-5xl text-[#f3d9a4] leading-[1.05]"
            style={{ fontFamily: 'var(--font-heading)' }}
            data-testid="lab-landing-title"
          >
            {t('labTitle')}
          </h1>
          <p
            className="mt-2 text-amber-200/70 text-base"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {t('labName')}
          </p>
          <p className="mt-4 text-amber-100/70 font-serif leading-relaxed text-base">
            {t('labTagline')}
          </p>
        </div>

        {/* Nimny portrait card */}
        <Card className="mt-8 p-3 overflow-hidden">
          <div className="relative rounded-xl overflow-hidden">
            <img
              src="/lab-hero.jpg"
              alt={t('brand')}
              loading="eager"
              decoding="async"
              className="block w-full h-auto select-none"
              draggable="false"
              data-testid="lab-landing-hero-image"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-xl"
              style={{
                boxShadow: 'inset 0 0 60px rgba(243, 217, 164, 0.12)'
              }}
            />
          </div>
        </Card>

        {/* CTA */}
        <div className="mt-8 space-y-3">
          {continueTarget ? (
            <Link
              to={continueTarget}
              className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] text-[#1a1208] font-sans transition-colors"
              data-testid="lab-landing-continue"
            >
              {user.role === 'parent' ? t('parentDashTitle') : t('menuHome')}
              <ChevronRight className="w-4 h-4" />
            </Link>
          ) : (
            <Link
              to="/lab/login"
              className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] text-[#1a1208] font-sans transition-colors"
              data-testid="lab-landing-signin"
            >
              {t('signIn')}
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        {/* Privacy footer note */}
        <div className="mt-10 flex items-center gap-2 text-xs text-amber-200/45 font-sans justify-center">
          <span>★</span>
          <span>
            <span className="lab-brand-mark">Nimny</span>{' '}
            {t('nimnyLightsTheWay')}
          </span>
        </div>

        {/* Copyright */}
        <p
          className="mt-6 text-center text-amber-200/30 font-sans text-xs"
          data-testid="lab-copyright"
        >
          © 2026 Nimny LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LabLanding;
